import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import {
  DecoratedRequestProvider,
  useDecoratedRequestContext,
} from 'src/features/requests/use-decorated-request-context';
import { useRequestQueryMutationDecorator } from 'src/features/requests/use-request-query-mutation-decorator';
import { Button, ButtonProps } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';
import { If } from 'src/components/If';
import { Breadcrumbs } from '@aag-development/becreatives-ui';
import { appRoutes } from 'src/routes';
import { useCompanyId } from 'src/features/companies/use-company-id';
import { Link } from 'src/components/ui/link';
import { RequestAttentionAction } from 'src/pages/RequestPage/RequestActionComponents/RequestAttentionAction';
import { authQueries, useRequestQuery } from 'src/api/queries';
import { useMakeQuery } from 'src/api/queries/useMakeQuery';
import {
  RequestResponse,
  requestClient,
  requestOutcomeStatus,
  requestStatus,
} from 'src/lib/services/api/request-api';
import { useOpenRequestStatusChangeModal } from 'src/features/requests/request-status-change/use-open-request-status-change-modal';
import { useRequestStatusMap } from 'src/pages/RequestPage/RequestStatusChangeForm/RequestStatusChangeForm.utils';
import { getRequestOutcomesQueryKey } from 'src/features/requests/request-outcome/use-request-outcomes-query-context';
import { deliverableOutcomesFilters } from 'src/features/requests/request-outcome/filter-queries';
import { useProfile } from 'src/models/profile';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { ProjectTitleCard } from 'src/features/requests/project-title-card';
import { requestContentTab } from 'src/pages/request/request-page/request-page.utils';
import {
  RequestAttentionRequestedAlert,
  RequestDetailsCard,
  RequestDetailsCardContent,
  RequestExtrasCard,
  RequestExtrasCardContent,
  RequestInformationCard,
  RequestInformationCardContent,
  RequestMediaSourceCard,
  RequestMediaSourceCardContent,
} from 'src/pages/request/request-page/request-default-page';

const RequestOutcomePageContentTop = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = useCompanyId();
  const request = useDecoratedRequestContext();
  const client = useQueryClient();
  const requestActions = useRequestQueryMutationDecorator(request.id);
  const { openRequestStatusChangeModal } = useOpenRequestStatusChangeModal();
  const { canChangeStatus, getAvailableStatuses } = useRequestStatusMap();
  const user = useProfile({ enabled: false });

  // A multi-outcome project allows only one outcome in editing at a time — the
  // queued deliverable card enforces this client-side (be-core doesn't), so the
  // status modal on this page must honour the same rule for the queued→editing
  // transition or it becomes a second, unguarded path to start a sibling
  // editing (#2524 review). Only a queued outcome is gated here, so the parent's
  // deliverables are fetched only in that case.
  const isQueuedOutcome = request.status === requestStatus.queued;
  const parentDeliverables = useMakeQuery(
    getRequestOutcomesQueryKey(request.parent_id ?? '', deliverableOutcomesFilters),
    ({ signal }) =>
      requestClient.outcomes(request.parent_id ?? '', {
        signal,
        params: deliverableOutcomesFilters,
      }),
    { enabled: !!request.parent_id && isQueuedOutcome, retry: false, staleTime: 60 * 1000 },
  );
  const hasSiblingInEditing = (parentDeliverables.data?.items ?? []).some(
    (o) => o.id !== request.id && o.status === requestOutcomeStatus.currentlyEditing,
  );
  // Fail closed: until the sibling check has actually resolved, keep the
  // queued→editing path closed. Otherwise a cold load / direct navigation
  // leaves the button enabled before the guard knows about a sibling (the
  // query's `data` is undefined → hasSiblingInEditing false). (#2524 review)
  const siblingCheckPending = isQueuedOutcome && !parentDeliverables.isSuccess;

  // First applicable reason wins, so the tooltip shows the most fundamental
  // blocker rather than whichever guard happens to run last.
  let changeStatusDisabledReason: string | undefined;
  if (user.isAdmin && !request.hasEditor) {
    changeStatusDisabledReason = t('warning.request.change_status.unsigned_editor', {
      ns: 'alerts',
    })!;
  } else if (!canChangeStatus(request.status)) {
    changeStatusDisabledReason = t('warning.request.change_status.customer_action_required', {
      ns: 'alerts',
    })!;
  } else if (isQueuedOutcome && hasSiblingInEditing) {
    changeStatusDisabledReason = t('There is already an outcome in editing status')!;
  }

  // Also disable when the outcome has no valid transitions at all — an archived
  // outcome still routes here for multi-outcome projects, and opening the modal
  // on it would offer an empty status list.
  const hasNoAvailableStatuses = getAvailableStatuses(request.status).length === 0;
  const changeStatusDisabled =
    !!changeStatusDisabledReason || siblingCheckPending || hasNoAvailableStatuses;

  // Surface the same status-change control the revisions page uses, so editors
  // can advance an outcome's workflow state directly from its Information page
  // without detouring through Revisions (#2524). An outcome is a request-like
  // entity, so the shared modal accepts its id as requestId. The parent
  // project's outcome lists are refreshed by useChangeRequestStatusMutation's
  // onSuccess (it refetches [requestOutcomes, parent_id] on every transition),
  // so no onSubmit refetch is needed here.
  const changeStatusButtonProps: ButtonProps = {
    variant: 'secondary',
    disabled: changeStatusDisabled,
    onClick: () => openRequestStatusChangeModal({ requestId: request.id, companyId }),
  };

  const attentionLabel = request.needsAttention
    ? (t('models/request:state.requested_attention_at', {
        defaultValue: 'Needs attention',
      }) as string)
    : (t('actions:request_attention') as string);

  return (
    <div ref={ref} className={cn('tw-flex tw-flex-wrap tw-gap-4', className)} {...props}>
      <If
        when={!!children}
        else={
          <>
            <Breadcrumbs
              crumbs={[
                {
                  text: request?.title ?? t('unset', { ns: 'common' })!,
                  className: 'fw-bold fs-5',
                },
              ]}
              back={() => {
                if (location.state && location.state.from) {
                  navigate(location.state.from);
                } else {
                  navigate(
                    generatePath(appRoutes.request, {
                      requestId: request.parent_id ?? '',
                      companyId,
                    }),
                  );
                }
              }}
              className={'mb-4 tw-truncate'}
            />

            <div
              className={
                'tw-flex tw-grow tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 sm:tw-justify-end md:tw-gap-4'
              }
            >
              <If when={request.canRequestAttention}>
                <RequestAttentionAction
                  form={{
                    requestId: request.id,
                    initialValues: {
                      attention_text: request.attention_text || '',
                    },
                    onSubmit: async ({ attention_text }) => {
                      requestActions.setData((prev) => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          attention_text,
                          requested_attention_at: new Date().toISOString(),
                        };
                      });
                      client.invalidateQueries({ queryKey: [authQueries.requests] });
                    },
                  }}
                  as={Button}
                  variant={request.needsAttention ? 'destructive' : 'outline'}
                  size={'iconStartSm'}
                  title={attentionLabel}
                  aria-label={attentionLabel}
                >
                  <Megaphone className={'tw-size-5'} />
                  {attentionLabel}
                </RequestAttentionAction>
              </If>

              <If
                when={!!changeStatusDisabledReason}
                else={
                  <Button {...changeStatusButtonProps}>
                    {t('change_status', { ns: 'actions', defaultValue: 'Change status' })}
                  </Button>
                }
              >
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      {/* A disabled Button has pointer-events:none, so the
                          tooltip must hang off an enabled wrapper to surface
                          the disabled reason on hover (#2524 review). */}
                      <span tabIndex={0}>
                        <Button {...changeStatusButtonProps} aria-label={changeStatusDisabledReason}>
                          {t('change_status', { ns: 'actions', defaultValue: 'Change status' })}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{changeStatusDisabledReason}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </If>

              <Button asChild variant={'accent1'}>
                <Link
                  variant={'unset'}
                  to={generatePath(appRoutes.requestRevisions, {
                    requestId: request.id,
                    companyId,
                  })}
                >
                  {t('models/revision:name.plural')}
                </Link>
              </Button>
            </div>
          </>
        }
      >
        {children}
      </If>
    </div>
  );
});
RequestOutcomePageContentTop.displayName = 'RequestOutcomePageContentTop';

// Surfaces the parent multi-outcome project's title above the outcome's
// Information accordion, replacing the previous "Parent request" link in
// the Media Source card. Karina's spec on
// AAG-Ventures/ai-agents-backend-service-v1#2431.
//
// Outcome detail view. Information is the outcome's own data; Extras /
// Media Source / Details are inherited from the parent project (those
// blocks live at the project level, not per-outcome). Wrapping those
// cards in a DecoratedRequestProvider with the parent request swaps the
// data source without duplicating the card components.
const RequestOutcomePageMainContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => {
  const { t } = useTranslation();
  const outcome = useDecoratedRequestContext();
  const parentQuery = useRequestQuery(outcome.parent_id ?? '', {
    enabled: !!outcome.parent_id,
  });

  return (
    <div ref={ref} {...props}>
      <Tabs
        defaultValue={requestContentTab.summary}
        className={'tw-flex tw-h-full tw-flex-col tw-gap-2'}
      >
        <TabsList
          className={
            'tw-hidden tw-w-full tw-gap-2 tw-overflow-x-auto tw-overflow-y-clip tw-bg-background tw-p-2'
          }
        >
          <TabsTrigger className={'tw-grow'} value={requestContentTab.summary}>
            {t(`common:${requestContentTab.summary}`, { defaultValue: 'Summary' })}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          className={'tw-relative tw-grow'}
          value={requestContentTab.summary}
        >
          <div className={'tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-4 tw-overflow-auto'}>
            <RequestAttentionRequestedAlert />

            {parentQuery.data && outcome.parent_id && (
              <ProjectTitleCard title={parentQuery.data.title} parentId={outcome.parent_id} />
            )}

            <RequestInformationCard>
              <RequestInformationCardContent />
            </RequestInformationCard>

            {parentQuery.data && (
              // OrderResponse (from useRequestQuery) and RequestResponse are
              // two near-identical admin-side shapes for the same backend
              // entity; the few extra fields RequestResponse declares aren't
              // read by the cards below. Cast through unknown rather than
              // duplicating a parallel decorator just for outcomes.
              <DecoratedRequestProvider
                request={parentQuery.data as unknown as RequestResponse}
              >
                <RequestExtrasCard>
                  <RequestExtrasCardContent />
                </RequestExtrasCard>

                <RequestMediaSourceCard>
                  <RequestMediaSourceCardContent />
                </RequestMediaSourceCard>

                <RequestDetailsCard>
                  <RequestDetailsCardContent />
                </RequestDetailsCard>
              </DecoratedRequestProvider>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
});
RequestOutcomePageMainContent.displayName = 'RequestOutcomePageMainContent';

export { RequestOutcomePageContentTop, RequestOutcomePageMainContent };
